import Icon from '@atoms/Icon/Icon';
import Tag from '@atoms/Tag/Tag';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import { useEffect, useState } from 'react';

interface Props {
  readonly amountSaved: number;
  readonly dataTestId?: string;
  readonly formattedAmountSaved: string;
  readonly hidePriceFromSavePill: boolean;
  readonly isRefurbished: boolean;
  readonly limitedDisplay?: boolean;
  readonly name: string;
  readonly refurbishedIcon: string;
  readonly refurbishedLabel: string;
  readonly savePriceLabel: string;
  readonly skuCode: string;
  readonly skuLabel: string;
}

const ProductDetailsTitle = ({
  amountSaved,
  dataTestId = 'Product-Details-Title',
  formattedAmountSaved,
  hidePriceFromSavePill,
  isRefurbished,
  limitedDisplay,
  name,
  refurbishedIcon,
  refurbishedLabel,
  savePriceLabel,
  skuCode,
  skuLabel,
}: Props) => {
  const [showSaveTag, setShowSaveTag] = useState(false);

  useEffect(() => {
    if (
      !limitedDisplay &&
      formattedAmountSaved &&
      savePriceLabel &&
      amountSaved > 0
    ) {
      setShowSaveTag(true);
    }
  }, [limitedDisplay, formattedAmountSaved, savePriceLabel, amountSaved]);

  return (
    <div data-testid={dataTestId}>
      <div className="mb-10 flex w-full flex-row flex-wrap items-center gap-2.5 xl:gap-5">
        {refurbishedLabel && isRefurbished && (
          <Tag
            pointerStates={false}
            className="bg-grey-100 text-black-100 dark:bg-white"
            dataTestId={`${dataTestId}-Refurbished-Tag`}
          >
            <div
              className="flex flex-row items-center"
              data-testid={`${dataTestId}-Refurbished-Tag-Label`}
            >
              {refurbishedIcon && (
                <Icon
                  name={refurbishedIcon}
                  colorClassName="text-yellow"
                  size={IconSizeEnum.md}
                  className="mr-2.5"
                />
              )}
              {refurbishedLabel}
            </div>
          </Tag>
        )}
        {showSaveTag && (
          <Tag
            className="bg-grey-600 text-yellow"
            pointerStates={false}
            text={`${savePriceLabel}${
              hidePriceFromSavePill ? '' : `: ${formattedAmountSaved}`
            }`}
            dataTestId={`${dataTestId}-Savings-Tag`}
          />
        )}
        <div
          className="body-four ml-auto hidden whitespace-nowrap text-grey-450 dark:text-grey-500 xl:block"
          data-testid={`${dataTestId}-SKU-Label/Code`}
        >
          {`${skuLabel}: ${skuCode}`}
        </div>
      </div>
      {name && (
        <h1
          className="h4 w-full text-left"
          data-testid={`${dataTestId}-Product-Title`}
        >
          {name}
        </h1>
      )}
    </div>
  );
};

export default ProductDetailsTitle;
